import { useTranslation } from "react-i18next";
import { useAcornBalance } from "@/contexts/AcornContext";
import { useProfile } from "@/hooks/queries/useProfile";
import type { MemoryWithChildren } from "@/hooks/useMemories";
import {
  CARD_COLORS,
  bodyStyle,
  cardShellStyle,
  labelStyle,
  titleStyle,
} from "./types";

interface StreakCardProps {
  memories: MemoryWithChildren[];
}

const StreakCard = ({ memories }: StreakCardProps) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const streak = profile?.current_streak ?? null;
  const { balance: acornBalance } = useAcornBalance();
  const colors = CARD_COLORS.streak;

  // Compute 7 day-of-week dots — last 7 days, today on the right.
  const days = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - (6 - i));
      return d;
    });
  })();

  const memoryDays = new Set(
    memories.map((m) => {
      const d = new Date(m.created_at);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }),
  );

  const dotFilled = days.map((d) => memoryDays.has(d.getTime()));

  return (
    <div style={cardShellStyle(colors.bg, colors.border)}>
      <div className="flex items-center justify-between">
        <div style={labelStyle(colors.body)}>{t("streak.label")}</div>
        <div
          className="flex items-center gap-1 rounded-full px-2 py-0.5"
          style={{
            backgroundColor: colors.border,
            fontSize: 12,
            fontWeight: 600,
            color: colors.title,
          }}
        >
          <span>🌰</span>
          <span>{acornBalance}</span>
        </div>
      </div>

      <div className="flex items-baseline gap-2 mt-2">
        <span
          style={{
            fontSize: 40,
            fontWeight: 700,
            color: colors.title,
            lineHeight: 1,
          }}
        >
          {streak ?? 0}
        </span>
        <span style={{ ...bodyStyle(colors.body), fontSize: 12 }}>
          {t("streak.day", { count: streak ?? 0 })}
        </span>
      </div>

      <p style={{ ...titleStyle(colors.title), fontSize: 13, marginTop: 4 }}>
        {(streak ?? 0) === 0 ? t("streak.waiting") : t("streak.burning")}
      </p>

      {/* 7 day-of-week dots */}
      <div className="flex items-center gap-2 mt-auto pt-2">
        {dotFilled.map((filled, i) => (
          <span
            key={i}
            aria-label={filled ? "recorded" : "no memory"}
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: filled ? colors.title : "transparent",
              border: `1.5px solid ${filled ? colors.title : colors.border}`,
              flexShrink: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default StreakCard;
